import { Sentry } from 'lib-shared-sentry';
import type { ReadConversationApiResponse } from 'lib-shared-types-api';

import type { AppState } from 'bootstrap.store';

import {
  getOutboundCallNumberForCsid,
  parseCsid,
  readOutboundCallNumberFromGetConversationResponse,
  resolveConversationLedTokenRegionFromState,
  resolveOutboundTokenRegionFromState,
  resolveTokenRegionForOwnedE164,
} from '../resolveTokenRegion';
import type { TwilioTokenRegion } from '../types';

// Emits a breadcrumb rather than an error because a fallback is expected in
// normal operation (e.g. stale Redux state). The breadcrumb is attached to any
// subsequent Sentry event for that session, giving context without creating noise.
function logTokenRegionFallback(reason: string, context: string): void {
  Sentry.addBreadcrumb({
    category: 'twilio',
    message: 'twilio_token_region_fallback',
    level: 'info',
    data: { reason, context },
  });
}

type RegionResolverDeps = {
  getState: () => AppState;
  fetchOwnNumbers: (payload?: Record<string, unknown>) => Promise<unknown>;
  readConversation: (csid: number) => Promise<ReadConversationApiResponse>;
};

/**
 * Resolves the token region for an agent-initiated outbound call given the
 * caller's E.164 number.
 */
type ResolveOutboundTokenRegion = (
  fromNumber: string,
) => Promise<TwilioTokenRegion | undefined>;

/**
 * Resolves the token region for a call driven by a conversation ID (inbound /
 * connect-call / make-callback flows).
 *
 * `context` is a human-readable label (e.g. `"connectCall"`) attached to any
 * fallback breadcrumbs logged to Sentry.
 */
type ResolveConversationLedTokenRegion = (
  csid: string | number,
  context: string,
) => Promise<TwilioTokenRegion | undefined>;

/** The two region-resolution strategies exposed by createRegionResolver. */
export type RegionResolver = {
  resolveOutboundTokenRegion: ResolveOutboundTokenRegion;
  resolveConversationLedTokenRegion: ResolveConversationLedTokenRegion;
};

/**
 * Factory that creates a RegionResolver — a pair of functions responsible for
 * determining which Twilio token region to use before placing or accepting a
 * call.
 *
 * Both resolvers follow the same layered strategy:
 *
 * 1. Check Redux state (zero-cost, synchronous).
 * 2. If unresolved, fetch the missing data (phone numbers or conversation) from
 *    the API.
 * 3. Re-check state after the fetch, then give up and log a breadcrumb if still
 *    unresolved.
 *
 * Dependencies are injected so the resolver can be unit-tested without live API
 * calls.
 */
export function createRegionResolver(deps: RegionResolverDeps): RegionResolver {
  const { getState, fetchOwnNumbers, readConversation } = deps;

  /**
   * Returns the outbound-call E.164 string for a conversation (i.e.
   * `callback_as_number` when present, otherwise `provisioned_number`),
   * checking Redux first to avoid a redundant network round-trip when the
   * conversation is already loaded. Falls back to the GET
   * /v2/conversations/:csid API otherwise.
   */
  async function fetchOutboundCallNumberForConversation(
    id: number,
    context: string,
  ): Promise<string | undefined> {
    const fromState = getOutboundCallNumberForCsid(getState(), id);
    if (fromState != null) {
      return fromState;
    }

    try {
      const response = await readConversation(id);
      const outboundE164 = readOutboundCallNumberFromGetConversationResponse(
        response.data,
      );

      if (outboundE164 == null) {
        logTokenRegionFallback('no_outbound_number_in_api_response', context);
      }

      return outboundE164;
    } catch {
      logTokenRegionFallback('get_conversation_api_failed', context);
      return undefined;
    }
  }

  /**
   * Resolves the token region for an agent-initiated outbound call. Matches
   * `fromNumber` against owned phone numbers in Redux; if no match is found
   * (e.g. numbers haven't loaded yet), refetches them once before giving up.
   */
  async function resolveOutboundTokenRegion(
    fromNumber: string,
  ): Promise<TwilioTokenRegion | undefined> {
    let region = resolveOutboundTokenRegionFromState(getState(), fromNumber);

    if (region) {
      return region;
    }

    try {
      await fetchOwnNumbers({});
    } catch {
      logTokenRegionFallback('refetch_numbers_failed', 'makeCall');
      return undefined;
    }

    region = resolveOutboundTokenRegionFromState(getState(), fromNumber);

    if (!region) {
      logTokenRegionFallback('unresolved_after_refetch', 'makeCall');
    }

    return region;
  }

  /**
   * Resolves the token region for an inbound or conference call driven by a
   * conversation ID (connect-call / make-callback flows).
   *
   * Strategy:
   *
   * 1. Try Redux directly — works when the conversation is already open.
   * 2. Fetch the outbound-call number from the conversation API if not in state.
   * 3. Match that number against owned numbers in Redux.
   * 4. If still unresolved, refetch owned numbers once and retry both paths.
   *
   * `resolveFromOutboundNumber` is captured as a closure so steps 3 and 4 can
   * reuse the same number without re-fetching the conversation.
   */
  async function resolveConversationLedTokenRegion(
    csid: string | number,
    context: string,
  ): Promise<TwilioTokenRegion | undefined> {
    // Parse once up front so all downstream helpers receive a consistent number.
    const id = parseCsid(csid);

    if (Number.isNaN(id)) {
      logTokenRegionFallback('invalid_csid', context);
      return undefined;
    }

    let region = resolveConversationLedTokenRegionFromState(getState(), id);

    if (region) {
      return region;
    }

    const outboundE164 = await fetchOutboundCallNumberForConversation(
      id,
      context,
    );

    const resolveFromOutboundNumber = (): TwilioTokenRegion | undefined => {
      if (outboundE164 == null) {
        return undefined;
      }
      return resolveTokenRegionForOwnedE164(getState(), outboundE164);
    };

    region = resolveFromOutboundNumber();

    if (region) {
      return region;
    }

    // The outbound-call number is known but no owned number matches it yet —
    // owned numbers may not have loaded. Refetch them and retry.
    try {
      await fetchOwnNumbers({});
    } catch {
      logTokenRegionFallback('refetch_numbers_failed', context);
      return undefined;
    }

    region = resolveConversationLedTokenRegionFromState(getState(), id);

    if (region) {
      return region;
    }

    region = resolveFromOutboundNumber();
    if (!region) {
      logTokenRegionFallback('unresolved_after_refetch', context);
    }
    return region;
  }

  return {
    resolveOutboundTokenRegion,
    resolveConversationLedTokenRegion,
  };
}
